import { toApiError } from '../errorHandling.js';
import { buildApiUrl } from '../../config/api.js';

export interface OutlineSection {
  title: string;
  description: string;
  section_planning: string;
  number_of_paragraphs: number;
  key_points: string[];
  supporting_data_points: string[];
  word_count: number;
  logical_connection: string;
  search_terms: string[];
}

export interface Outline {
  title: string;
  total_word_count: number;
  sections: OutlineSection[];
  metadata?: {
    lastReview?: {
      score: number;
      wasImproved: boolean;
      timestamp: string;
      verdict: string;
    };
  };
}

export interface OutlineSectionUpdate {
  index: number;
  updates: Partial<OutlineSection>;
}

export interface OutlinePatchRequest {
  title?: string;
  sections?: OutlineSectionUpdate[];
}

export interface OutlineExistsResponse {
  exists: boolean;
}

export interface OutlineLoadResponse {
  outline: Outline;
}

/**
 * Service for managing article outlines
 */
class OutlineService {
  private async getAuthHeaders(getToken?: () => Promise<string | null>): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (getToken) {
      const token = await getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return headers;
  }

  async patch(
    briefId: string,
    updates: OutlinePatchRequest,
    getToken?: () => Promise<string | null>
  ): Promise<Outline> {
    try {
      const headers = await this.getAuthHeaders(getToken);
      const response = await fetch(
        buildApiUrl(`/api/outline/${encodeURIComponent(briefId)}`),
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update outline: ${response.statusText}`);
      }

      const data = await response.json();
      return data.outline;
    } catch (error) {
      console.error('Error patching outline:', error);
      throw toApiError(error);
    }
  }

  async addSection(
    briefId: string,
    options: { position: number; section?: Partial<OutlineSection> },
    getToken?: () => Promise<string | null>
  ): Promise<Outline> {
    try {
      const headers = await this.getAuthHeaders(getToken);
      const response = await fetch(
        buildApiUrl(`/api/outline/${encodeURIComponent(briefId)}/sections`),
        {
          method: 'POST',
          headers,
          body: JSON.stringify(options),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add section: ${response.statusText}`);
      }

      const data = await response.json();
      return data.outline;
    } catch (error) {
      console.error('Error adding section:', error);
      throw toApiError(error);
    }
  }

  async deleteSection(
    briefId: string,
    sectionIndex: number,
    getToken?: () => Promise<string | null>
  ): Promise<Outline> {
    try {
      const headers = await this.getAuthHeaders(getToken);
      const response = await fetch(
        buildApiUrl(`/api/outline/${encodeURIComponent(briefId)}/sections/${sectionIndex}`),
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.details || `Failed to delete section: ${response.statusText}`);
      }

      const data = await response.json();
      return data.outline;
    } catch (error) {
      console.error('Error deleting section:', error);
      throw toApiError(error);
    }
  }

  async reorderSections(
    briefId: string,
    order: number[],
    getToken?: () => Promise<string | null>
  ): Promise<Outline> {
    try {
      const headers = await this.getAuthHeaders(getToken);
      const response = await fetch(
        buildApiUrl(`/api/outline/${encodeURIComponent(briefId)}/sections/reorder`),
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ order }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to reorder sections: ${response.statusText}`);
      }

      const data = await response.json();
      return data.outline;
    } catch (error) {
      console.error('Error reordering sections:', error);
      throw toApiError(error);
    }
  }

  /**
   * Check if an outline exists
   */
  async checkExists(
    briefId: string,
    getToken?: () => Promise<string | null>
  ): Promise<boolean> {
    try {
      const headers: HeadersInit = {};

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(
        buildApiUrl(`/api/outline/${encodeURIComponent(briefId)}/exists`),
        { headers }
      );

      if (response.status === 404) {
        return false;
      }

      if (!response.ok) {
        throw new Error(`Failed to check outline existence: ${response.statusText}`);
      }

      const data: OutlineExistsResponse = await response.json();
      return Boolean(data?.exists);
    } catch (error) {
      console.error('Error checking outline existence:', error);
      throw toApiError(error);
    }
  }

  /**
   * Load an existing outline for a given brief ID
   */
  async getByBriefId(
    briefId: string,
    getToken?: () => Promise<string | null>
  ): Promise<Outline> {
    try {
      const headers: HeadersInit = {};

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(
        buildApiUrl(`/api/outline/${encodeURIComponent(briefId)}`),
        { headers }
      );

      if (!response.ok) {
        throw new Error(`Failed to load outline: ${response.statusText}`);
      }

      const data: OutlineLoadResponse = await response.json();
      return data.outline;
    } catch (error) {
      console.error('Error loading outline:', error);
      throw toApiError(error);
    }
  }

  /**
   * Generate a new outline for a given brief ID
   * Returns an EventSource for streaming the generation process
   */
  generateStream(briefId: string): EventSource {
    const url = buildApiUrl(`/api/outline/generate-outline/stream?id=${briefId}`);

    return new EventSource(url);
  }

  /**
   * Delete an outline for a given brief ID
   */
  async delete(
    briefId: string,
    getToken?: () => Promise<string | null>
  ): Promise<void> {
    try {
      const headers: HeadersInit = {};

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(
        buildApiUrl(`/api/outline/${encodeURIComponent(briefId)}`),
        {
          method: 'DELETE',
          headers,
        }
      );

      if (!response.ok && response.status !== 404) {
        throw new Error(`Failed to delete outline: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting outline:', error);
      throw toApiError(error);
    }
  }

  /**
   * Load outline and brief data together
   */
  async loadBriefAndOutline(
    briefId: string,
    getToken?: () => Promise<string | null>
  ): Promise<{ outline: Outline; brief: any }> {
    try {
      const headers: HeadersInit = {};

      // Add auth token if available
      if (getToken) {
        const token = await getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const url = buildApiUrl(`/api/article-briefs/${briefId}/outline`);
      console.log('🌐 [OUTLINE SERVICE] Fetching outline from:', url);
      console.log('🌐 [OUTLINE SERVICE] Brief ID:', briefId);

      const response = await fetch(url, { headers });

      console.log('📡 [OUTLINE SERVICE] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [OUTLINE SERVICE] Error response:', errorText);
        throw new Error(`Failed to load brief outline: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [OUTLINE SERVICE] Response data received:', {
        hasOutline: Boolean(data.outline),
        hasBrief: Boolean(data.brief),
        outlineTitle: data.outline?.title,
        sectionsCount: data.outline?.sections?.length,
        briefId: data.brief?.id
      });

      return {
        outline: data.outline,
        brief: data.brief
      };
    } catch (error) {
      console.error('❌ [OUTLINE SERVICE] Error loading brief and outline:', error);
      console.error('❌ [OUTLINE SERVICE] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack'
      });
      throw toApiError(error);
    }
  }
}

// Export singleton instance
export const outlineService = new OutlineService();